import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import LoadIndicator from 'devextreme-react/load-indicator';
import {
  fileAccessRestrictedHref,
  isFileAccessRestrictedStatus
} from '../storageAccess/fileAccessRestrictedUrls';
import { MediaViewerChromeBar } from './MediaViewerChromeBar';
import { displayNameFromPath } from './mediaFileTypes';
import type { MediaKind } from './mediaFileTypes';
import { useMediaFileMetadata } from './mediaViewerMetadata';
import { mediaPreviewSrc } from './mediaPreviewUrls';
import { useMediaImageDisplaySrc } from './useMediaImageDisplaySrc';
import { useMediaVideoLoadState } from './useMediaVideoLoadState';
import './MediaViewerPage.css';
import './mediaViewerStage.css';

export interface MediaViewerPageProps {
  kind: MediaKind;
  filesLinkTo?: string;
  filesLinkLabel?: string;
}

export function MediaViewerPage({
  kind,
  filesLinkTo = '/files',
  filesLinkLabel = 'File Browser'
}: MediaViewerPageProps) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const mountId = (params.get('mountId') ?? '').trim();
  const path = (params.get('path') ?? '').trim();

  const hasSource = Boolean(mountId && path);
  const imageDisplay = useMediaImageDisplaySrc(mountId, path, hasSource && kind === 'image');
  const imageSrc = imageDisplay.displaySrc;

  useEffect(() => {
    if (kind !== 'image' || imageDisplay.state !== 'error') return;
    if (imageDisplay.errorStatus != null && isFileAccessRestrictedStatus(imageDisplay.errorStatus)) {
      navigate(fileAccessRestrictedHref(), { replace: true });
    }
  }, [kind, imageDisplay.state, imageDisplay.errorStatus, navigate]);

  const videoSrc = useMemo(() => {
    if (!hasSource || kind !== 'video') return '';
    return mediaPreviewSrc(mountId, path);
  }, [hasSource, kind, mountId, path]);

  const onVideoAccessRestricted = useCallback(() => {
    navigate(fileAccessRestrictedHref(), { replace: true });
  }, [navigate]);

  const videoLoad = useMediaVideoLoadState(videoSrc, {
    onAccessRestricted: onVideoAccessRestricted
  });

  const [imageDecoded, setImageDecoded] = useState(false);
  const [imageDecodeFailed, setImageDecodeFailed] = useState(false);

  useEffect(() => {
    setImageDecoded(false);
    setImageDecodeFailed(false);
  }, [imageSrc]);

  const metadata = useMediaFileMetadata(mountId, path, hasSource);

  const title = path ? displayNameFromPath(path) : '';
  const kindLabel = kind === 'image' ? 'Image' : 'Video';
  const pageTitle = kind === 'image' ? 'JARVIS Image Viewer' : 'JARVIS Video Viewer';

  useEffect(() => {
    const previousTitle = document.title;
    document.title = pageTitle;
    return () => {
      document.title = previousTitle;
    };
  }, [pageTitle]);

  const loadState =
    kind === 'image'
      ? imageDisplay.state === 'error' || imageDecodeFailed
        ? 'error'
        : imageDisplay.state !== 'ready' || !imageDecoded
          ? 'loading'
          : 'loaded'
      : videoLoad.state === 'idle'
        ? 'loading'
        : videoLoad.state;

  const actionsDisabled = loadState !== 'loaded';

  const onImageMount = useCallback((el: HTMLImageElement | null) => {
    if (el?.complete && el.naturalWidth > 0) {
      setImageDecoded(true);
    }
  }, []);

  if (!hasSource) {
    return (
      <div className="media-viewer-page media-viewer-page--empty">
        <p className="page-sub">No {kind} selected.</p>
        <p className="page-sub">
          Open a file from <Link to={filesLinkTo}>{filesLinkLabel}</Link> — use <strong>Preview</strong> or{' '}
          <strong>Open in {kindLabel} Viewer</strong>.
        </p>
      </div>
    );
  }

  return (
    <div className="ops-fill-page ops-fill-page--bare media-viewer-page media-viewer-page--active">
      <div className="media-viewer-page__header">
        <span className="media-viewer-page__title" title={path}>
          {title}
        </span>
      </div>
      <MediaViewerChromeBar
        kind={kind}
        mountId={mountId}
        relativePath={path}
        metadata={metadata}
        disabled={actionsDisabled}
      />
      <div className="media-viewer-stage">
        {loadState === 'loading' ? (
          <div className="media-viewer-stage__loading" aria-busy="true" aria-live="polite">
            <LoadIndicator visible height={28} width={28} />
            <span>Loading {kind}…</span>
          </div>
        ) : null}
        {loadState === 'error' ? (
          <p className="media-viewer-stage__error">
            {kind === 'video'
              ? (videoLoad.errorMessage ?? 'Could not load this video. Check permissions or try again.')
              : (imageDisplay.errorMessage ??
                'Could not load this file. Check permissions or try again.')}
          </p>
        ) : (
          <div className="media-viewer-stage__fit">
            {kind === 'image' && imageSrc && imageDisplay.state === 'ready' ? (
              <img
                key={imageSrc}
                ref={onImageMount}
                className={`media-viewer-stage__img${loadState === 'loaded' ? ' media-viewer-stage__img--visible' : ''}`}
                src={imageSrc}
                alt=""
                onLoad={() => setImageDecoded(true)}
                onError={() => setImageDecodeFailed(true)}
              />
            ) : null}
            {videoSrc && kind === 'video' ? (
              <video
                key={videoSrc}
                ref={videoLoad.onVideoMount}
                className="media-viewer-stage__video"
                src={videoSrc}
                controls
                playsInline
                preload="metadata"
                onLoadedMetadata={videoLoad.onLoaded}
                onCanPlay={videoLoad.onLoaded}
                onError={videoLoad.onError}
              />
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
